import { Controller } from "@hotwired/stimulus";
import * as Luxon from "luxon";

export default class extends Controller {
  static conflictPreviewDebounceMs = 3000;

  static values = {
    classroomProgramListExpanded: { type: Boolean, default: false },
    driveTestDuration: { type: Number, default: 120 },
    driveTestPreTestDuration: { type: Number, default: 0 },
    schoolTimeZone: String,
    singleSlotMode: { type: Boolean, default: false },
  };

  static targets = [
    "slottimeselection",
    "slottimeselectiontest",
    "breakselection",
    "zonesselection",
    "pretestDurationSelection",
    "pretestDurationCheckbox",
    "slotVehicleId",
    "slotDaysInput",
    "premiumInstructorId",
    "flexibleShiftId",
    "errorMessageContainer",
    "errorMessage",
    "loader",
    "formButtons",
    "successContainer",
    "testRadioButton",
    "testRadioButton",
    "slotsSectionContainer",
    "slotsSectionContainerTest",
    "observationContainer",
    "observableCheckbox",

    "programsContainer",
    "programCheckboxes",
    "teenCheckbox",
    "adultCheckbox",
    "classroomProgramsList",
    "classroomProgramsToggleLabel",
    "classroomProgramCheckboxes",

    "currentCheckbox",
    "newCheckbox",
    "slotDuration",
    "adjacentSlotsOnlyCheckbox",
    "breakDurationRows",
    "breakDurationMinutes",
    "singleSlotDate",
    "singleSlotStartTime",

    "conflictPreviewState",
    "conflictPreviewSummary",
    "conflictPreviewPayloadMeta",
    "conflictPreviewPayloadContainer",
    "conflictPreviewPayload",
    "conflictPreviewResultContainer",
    "conflictPreviewResult",

    "duplicateWarning",
    "duplicateWarningMessage",
  ];

  apiErrors = [];
  changedElements = [];
  conflictPreviewDebounceTimer = null;
  conflictPreviewSendingTimer = null;
  conflictPreviewAbortController = null;
  conflictPreviewRunId = 0;
  lastLessonSlotDuration = null;
  lastDrivingTestSlotDuration = null;
  lastBreakSelectionValue = null;
  previousMode = null;

  connect() {
    this.syncAdjacentSlotsOnlyCheckboxState();
    this.syncPreTestDurationSelection();
    const initialDuration = this.hasSlotDurationTarget
      ? this.slotDurationTarget.value
      : null;
    const initialMode = this.hasTestRadioButtonTarget && this.testRadioButtonTarget.checked
      ? "driving_test"
      : "lesson";
    if (initialMode === "driving_test") {
      this.lastDrivingTestSlotDuration = initialDuration;
    } else {
      this.lastLessonSlotDuration = initialDuration;
    }
    this.previousMode = initialMode;
    this.syncSlotDurationInputState();
    this.syncBreakDurationRows({ force: true });
    this.syncSingleSlotFields();
    this.syncSelectedSlotDurations();
    this.syncClassroomProgramListVisibility();
    this.validateForm();
  }

  syncAdjacentSlotsOnlyCheckboxState() {
    if (!this.hasAdjacentSlotsOnlyCheckboxTarget) {
      return;
    }

    this.adjacentSlotsOnlyCheckboxTarget.checked =
      this.adjacentSlotsOnlyCheckboxTarget.dataset.adjacentSlotsOnlySelected === "true";
  }

  disconnect() {
    this.clearConflictPreviewTimers();
  }

  syncPreTestDurationSelection() {
    if (!this.hasPretestDurationSelectionTarget) {
      return;
    }

    const selectedDurations = this.hasPretestDurationCheckboxTarget
      ? this.pretestDurationCheckboxTargets
        .filter((checkbox) => checkbox.checked)
        .map((checkbox) => parseInt(checkbox.value, 10))
        .filter((value) => !Number.isNaN(value))
      : [];

    this.pretestDurationSelectionTarget.value = JSON.stringify(selectedDurations);
  }

  syncBreakDurationRows(options = {}) {
    if (!this.hasBreakselectionTarget || !this.hasBreakDurationRowsTarget) {
      return;
    }

    const breakSelectionValue = this.breakselectionTarget.value || "[]";
    if (!options.force && this.lastBreakSelectionValue === breakSelectionValue) {
      return;
    }

    this.lastBreakSelectionValue = breakSelectionValue;

    const selectedBreaks = this.parseJsonArray(breakSelectionValue);
    const selectedStartMinutes = selectedBreaks
      .map((slot) => this.startMinutesFromSlot(slot))
      .filter((startMinutes) => startMinutes !== null);
    const selectedStartMinuteSet = new Set(
      selectedStartMinutes.map((startMinutes) => String(startMinutes))
    );
    const existingDurations = this.breakDurationMap();

    Array.from(
      this.breakDurationRowsTarget.querySelectorAll(":scope > [data-break-start-minutes]")
    ).forEach((row) => {
      if (!selectedStartMinuteSet.has(String(row.dataset.breakStartMinutes))) {
        row.remove();
      }
    });

    selectedBreaks.forEach((slot) => {
      const startMinutes = this.startMinutesFromSlot(slot);
      if (startMinutes === null) {
        return;
      }

      const existingRow = this.breakDurationRowsTarget.querySelector(
        `[data-break-start-minutes="${startMinutes}"]`
      );
      if (existingRow) {
        return;
      }

      const duration = existingDurations.get(String(startMinutes)) || 15;
      this.breakDurationRowsTarget.insertAdjacentHTML(
        "beforeend",
        this.breakDurationRowMarkup(startMinutes, duration)
      );
    });

    this.breakDurationRowsTarget.classList.toggle(
      "u-hidden",
      selectedStartMinutes.length === 0
    );
  }

  breakDurationRowMarkup(startMinutes, duration) {
    const inputId = `break_duration_minutes_${startMinutes}`;

    return `
      <div
        class="InputGroup  InputGroup--block  mb1"
        data-break-start-minutes="${startMinutes}"
      >
        <label class="InputLabel" for="${inputId}">
          Duration for break at ${this.escapeHtml(this.timeLabelFromMinutes(startMinutes))}
        </label>
        <input
          type="number"
          min="1"
          id="${inputId}"
          value="${duration}"
          class="Input"
          style="width: 5rem;"
          data-break-start-minutes="${startMinutes}"
          data-driving-school-shifts-quick-look-target="breakDurationMinutes"
          data-action="change->driving-school-shifts-quick-look#validateForm input->driving-school-shifts-quick-look#validateForm"
        />
        minutes
      </div>
    `;
  }

  breakDurationMap() {
    const durations = new Map();

    if (!this.hasBreakDurationMinutesTarget) {
      return durations;
    }

    this.breakDurationMinutesTargets.forEach((input) => {
      const startMinutes = input.dataset.breakStartMinutes;
      const duration = parseInt(input.value, 10);
      if (startMinutes && !Number.isNaN(duration) && duration > 0) {
        durations.set(String(startMinutes), duration);
      }
    });

    return durations;
  }

  toggleClassroomProgramList(event) {
    event.preventDefault();
    this.classroomProgramListExpandedValue =
      !this.classroomProgramListExpandedValue;
    this.syncClassroomProgramListVisibility();
  }

  syncClassroomProgramListVisibility() {
    if (!this.hasClassroomProgramsListTarget) {
      return;
    }
    const expanded = this.classroomProgramListExpandedValue;
    this.classroomProgramsListTarget.classList.toggle("u-hidden", !expanded);
    if (this.hasClassroomProgramsToggleLabelTarget) {
      this.classroomProgramsToggleLabelTarget.textContent = expanded
        ? "Hide classrooms"
        : "Show classrooms";
    }
  }

  scheduleConflictPreview(event) {
    if (!this.shouldTrackConflictPreviewEvent(event)) {
      return;
    }

    this.beginConflictPreviewDebounce();
  }

  beginConflictPreviewDebounce() {
    this.conflictPreviewRunId += 1;
    this.clearConflictPreviewTimers();
    const previewState = this.buildConflictPreviewState();

    this.renderConflictPreviewState("debouncing", {
      summary:
        "Waiting for the user to stop editing things before the conflict preview request runs.",
      payload: previewState.previewPayload,
      result: null,
    });

    this.conflictPreviewDebounceTimer = window.setTimeout(() => {
      this.runConflictPreview(this.conflictPreviewRunId);
    }, this.constructor.conflictPreviewDebounceMs);
  }

  async runConflictPreview(runId) {
    if (runId !== this.conflictPreviewRunId) {
      return;
    }

    const previewState = this.buildConflictPreviewState();

    if (!previewState.canSend) {
      this.renderConflictPreviewState("no_send", {
        summary: `User not busy. Conflict check will not occur yet because the form is incomplete: ${previewState.missingReasons.join(
          ", "
        )}.`,
        payload: previewState.previewPayload,
        result: null,
      });
      return;
    }

      this.renderConflictPreviewState("sending", {
        summary:
        "User not busy. Here is the payload being sent asynchronously to the conflict service.",
        payload: previewState.previewPayload,
        result: null,
      });

    const authenticityTokenElement = document.querySelector(
      'meta[name="csrf-token"]'
    );
    const authenticityToken = authenticityTokenElement
      ? authenticityTokenElement.content
      : "";

    this.cancelConflictPreviewRequest();
    this.conflictPreviewAbortController = new AbortController();

    let responseJson;

    try {
      const result = await fetch("/schools/setup/shifts/flexible/preview-conflicts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": authenticityToken,
        },
        body: JSON.stringify({
          ...previewState.previewPayload,
          premium_instructor_id: this.premiumInstructorIdTarget.value,
          slot_days_input: this.slotDaysInputTarget.value,
          slottimeselection: this.testRadioButtonTarget.checked
            ? this.slottimeselectiontestTarget.value
            : this.slottimeselectionTarget.value,
          debug: true,
          authenticity_token: authenticityToken,
        }),
        signal: this.conflictPreviewAbortController.signal,
      });

      responseJson = await result.json();
    } catch (error) {
      if (error?.name === "AbortError" || runId !== this.conflictPreviewRunId) {
        return;
      }

      responseJson = {
        ok: false,
        error: error?.message || "Conflict preview request failed.",
      };
    }

    if (runId !== this.conflictPreviewRunId) {
      return;
    }

    this.conflictPreviewSendingTimer = window.setTimeout(() => {
      if (runId !== this.conflictPreviewRunId) {
        return;
      }

      this.conflictPreviewAbortController = null;

      this.renderConflictPreviewState("done_sending", {
        summary:
          "User not busy. Here is the payload that was sent, plus the JSON returned by the controller.",
        payload: previewState.previewPayload,
        result: responseJson,
      });
    }, 0);
  }

  validateForm(event) {
    this.syncSlotDurationInputState();
    this.syncBreakDurationRows();
    this.syncSingleSlotFields();
    this.syncSelectedSlotDurations();
    const errors = [];

    if (event?.currentTarget) {
      this.changedElements.push(event.currentTarget);
    }

    const allowsTeen = this.teenCheckboxTarget.checked;
    const allowsAdult = this.adultCheckboxTarget.checked;
    // Any program must be selected if neither teen nor adult only is selected
    let programSelected = false;
    this.programCheckboxesTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        programSelected = true;
      }
    });
    if (!allowsTeen && !allowsAdult && !programSelected) {
      errors.push("Please select at least one program");
    }

    const allowsCurrent = this.currentCheckboxTarget.checked;
    const allowsNew = this.newCheckboxTarget.checked;
    // if(!allowsCurrent && !allowsNew) {
    //   errors.push("Please select at least one student type");
    // }

    if (this.changedElements.includes(this.premiumInstructorIdTarget)) {
      const instructorIdValue = parseInt(this.premiumInstructorIdTarget.value, 10);
      if (isNaN(instructorIdValue) || instructorIdValue <= 0) {
        errors.push("Please select an instructor");
      }
    }

    const isTestSelected = this.testRadioButtonTarget.checked;
    const slotDurationValue = parseInt(this.slotDurationTarget.value, 10);
    if (isNaN(slotDurationValue) || slotDurationValue <= 0) {
      errors.push("Please enter a valid slot duration");
    }

    if (this.hasBreakselectionTarget) {
      const breaks = this.buildBreakPayload();
      const selectedBreaks = this.parseJsonArray(this.breakselectionTarget.value);
      if (breaks.length !== selectedBreaks.length) {
        errors.push("Please enter a valid break duration");
      }

      const sortedBreaks = [...breaks].sort(
        (left, right) => left.start_minutes - right.start_minutes
      );
      for (let index = 1; index < sortedBreaks.length; index += 1) {
        const previousBreak = sortedBreaks[index - 1];
        const currentBreak = sortedBreaks[index];
        if (
          previousBreak.start_minutes + previousBreak.duration_minutes >
          currentBreak.start_minutes
        ) {
          errors.push("Breaks cannot overlap");
          break;
        }
      }
    }
    const shouldValidateSingleSlotInputs =
      this.singleSlotModeValue &&
      [
        this.hasSingleSlotDateTarget ? this.singleSlotDateTarget : null,
        this.hasSingleSlotStartTimeTarget ? this.singleSlotStartTimeTarget : null,
        this.hasSlotDurationTarget ? this.slotDurationTarget : null,
        this.testRadioButtonTarget,
      ]
        .filter(Boolean)
        .some((target) => this.changedElements.includes(target));

    if (isTestSelected) {
      this.slotsSectionContainerTarget.classList.add("u-hidden");
      this.slotsSectionContainerTestTarget.classList.toggle(
        "u-hidden",
        this.singleSlotModeValue
      );
      this.observationContainerTarget.classList.add("u-hidden");
      if (shouldValidateSingleSlotInputs) {
        const slotDaysValue = this.parseJsonArray(this.slotDaysInputTarget.value);
        if (slotDaysValue.length === 0) {
          errors.push("Please select a date");
        }

        const slotTimesValue = this.parseJsonArray(
          this.slottimeselectiontestTarget.value
        );
        if (slotTimesValue.length === 0) {
          errors.push("Please select a start time");
        }
      } else if (this.changedElements.includes(this.slottimeselectiontestTarget)) {
        try {
          const slotTimesValue = JSON.parse(
            this.slottimeselectiontestTarget.value
          );
          if (slotTimesValue.length === 0) {
            errors.push("Please select at least one slot time");
          }
        } catch { }
      }
    } else {
      this.slotsSectionContainerTarget.classList.toggle(
        "u-hidden",
        this.singleSlotModeValue
      );
      this.slotsSectionContainerTestTarget.classList.add("u-hidden");
      this.observationContainerTarget.classList.remove("u-hidden");
      if (shouldValidateSingleSlotInputs) {
        const slotDaysValue = this.parseJsonArray(this.slotDaysInputTarget.value);
        if (slotDaysValue.length === 0) {
          errors.push("Please select a date");
        }

        const slotTimesValue = this.parseJsonArray(this.slottimeselectionTarget.value);
        if (slotTimesValue.length === 0) {
          errors.push("Please select a start time");
        }
      } else if (this.changedElements.includes(this.slottimeselectionTarget)) {
        try {
          const slotTimesValue = JSON.parse(this.slottimeselectionTarget.value);
          if (slotTimesValue.length === 0) {
            errors.push("Please select at least one slot time");
          }
        } catch { }
      }
    }

    if (this.changedElements.includes(this.zonesselectionTarget)) {
      try {
        const zonesValue = JSON.parse(this.zonesselectionTarget.value);
        if (zonesValue.length === 0) {
          errors.push("Please select at least one zone");
        }
      } catch { }
    }

    if (this.changedElements.includes(this.slotVehicleIdTarget)) {
      try {
        const slotVehicleIdValue = this.slotVehicleIdTarget.value;
        if (slotVehicleIdValue === "") {
          errors.push("Please select a vehicle");
        }
      } catch { }
    }

    if (!this.singleSlotModeValue && this.changedElements.includes(this.slotDaysInputTarget)) {
      try {
        const slotDaysValue = JSON.parse(this.slotDaysInputTarget.value);
        if (slotDaysValue.length === 0) {
          errors.push("Please select at least one day");
        }
      } catch { }
    }

    errors.push(...this.apiErrors);

    if (errors.length > 0) {
      this.errorMessageContainerTarget.classList.remove("u-hidden");
    } else {
      this.errorMessageContainerTarget.classList.add("u-hidden");
    }

    this.errorMessageTarget.innerHTML = "";
    errors.forEach((error) => {
      // Create a new p element
      const p = document.createElement("p");
      p.innerHTML = error;
      this.errorMessageTarget.appendChild(p);
    });

    this.scheduleConflictPreview(event);

    return errors;
  }

  buildConflictPreviewState() {
    this.syncSingleSlotFields();
    this.syncSelectedSlotDurations();
    const isDrivingTest = this.testRadioButtonTarget.checked;
    const selectedSlots = this.parseJsonArray(
      isDrivingTest
        ? this.slottimeselectiontestTarget.value
        : this.slottimeselectionTarget.value
    );
    const selectedBreaks = this.buildBreakPayload();
    const selectedZoneIds = this.parseJsonArray(this.zonesselectionTarget.value);
    const selectedDays = this.parseJsonArray(this.slotDaysInputTarget.value);
    const slotVehicleId = this.slotVehicleIdTarget.value;
    const slotDuration = parseInt(this.slotDurationTarget.value, 10);
    const programIds = this.programCheckboxesTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => parseInt(checkbox.value, 10))
      .filter((id) => !Number.isNaN(id));
    const classroomProgramIds = this.classroomProgramCheckboxesTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => parseInt(checkbox.value, 10))
      .filter((id) => !Number.isNaN(id));
    const allowsTeens = this.teenCheckboxTarget.checked;
    const allowsAdults = this.adultCheckboxTarget.checked;
    const allowsObservers =
      this.hasObservableCheckboxTarget && this.observableCheckboxTarget.checked;
    const instructorId = parseInt(this.premiumInstructorIdTarget.value, 10);
    const flexibleShiftId = this.hasFlexibleShiftIdTarget
      ? parseInt(this.flexibleShiftIdTarget.value, 10)
      : null;

    const missingReasons = [];
    if (Number.isNaN(instructorId) || instructorId <= 0) {
      missingReasons.push("missing instructor");
    }
    if (selectedZoneIds.length === 0) {
      missingReasons.push("missing zone");
    }
    if (!slotVehicleId) {
      missingReasons.push("missing vehicle");
    }
    if (selectedDays.length === 0) {
      missingReasons.push("missing day");
    }
    if (selectedSlots.length === 0) {
      missingReasons.push("missing slot");
    }
    if (Number.isNaN(slotDuration) || slotDuration <= 0) {
      missingReasons.push("missing valid duration");
    }
    if (!allowsTeens && !allowsAdults && programIds.length === 0) {
      missingReasons.push("missing availability selection");
    }

    const timeRanges = this.buildConflictPreviewTimeRanges({
      selectedDays,
      selectedSlots,
      selectedBreaks,
      isDrivingTest,
      slotDuration,
    });

    const previewPayload = {
      school_time_zone:
        this.schoolTimeZoneValue ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      flexible_shift_id:
        Number.isNaN(flexibleShiftId) || flexibleShiftId <= 0
          ? null
          : flexibleShiftId,
      instructor_id: instructorId,
      vehicle_id: slotVehicleId ? parseInt(slotVehicleId, 10) : null,
      is_driving_test: isDrivingTest,
      observable: allowsObservers,
      allows_teens: allowsTeens,
      allows_adults: allowsAdults,
      zone_ids: selectedZoneIds,
      selected_days: selectedDays,
      selected_slots: selectedSlots,
      selected_breaks: selectedBreaks,
      program_ids: programIds,
      classroom_program_ids: classroomProgramIds,
      resources: [
        {
          resource_type: "instructor",
          resource_id: instructorId,
        },
        {
          resource_type: "vehicle",
          resource_id: slotVehicleId ? parseInt(slotVehicleId, 10) : null,
        },
      ],
      single_slot_mode: this.singleSlotModeValue,
      time_ranges: timeRanges,
    };

    return {
      canSend: missingReasons.length === 0,
      missingReasons,
      previewPayload,
    };
  }

  buildConflictPreviewTimeRanges({
    selectedDays,
    selectedSlots,
    selectedBreaks,
    isDrivingTest,
    slotDuration,
  }) {
    const schoolTimeZone =
      this.schoolTimeZoneValue ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    return selectedDays.flatMap((selectedDay) => {
      const slotRanges = selectedSlots.map((slot) => {
        const timeRange = this.buildConflictPreviewTimeRange(
          selectedDay,
          slot,
          isDrivingTest,
          slotDuration,
          schoolTimeZone
        );
        return timeRange;
      });

      const breakRanges = selectedBreaks.map((shiftBreak) => {
        return this.buildConflictPreviewBreakTimeRange(
          selectedDay,
          shiftBreak,
          schoolTimeZone
        );
      });

      return slotRanges.concat(breakRanges);
    }).filter(Boolean);
  }

  buildConflictPreviewTimeRange(
    selectedDay,
    slot,
    isDrivingTest,
    slotDuration,
    schoolTimeZone
  ) {
    const hour = slot.hour ?? slot.test_hour;
    const minute = slot.minute ?? slot.test_minute;

    if (
      typeof selectedDay !== "string" ||
      hour === undefined ||
      minute === undefined
    ) {
      return null;
    }

    const startDateTime = this.buildSchoolZoneDateTime(
      selectedDay,
      parseInt(hour, 10),
      parseInt(minute, 10),
      schoolTimeZone
    );
    if (!startDateTime) {
      return null;
    }

    const startOffsetMinutes = isDrivingTest
      ? this.driveTestPreTestDurationValue || 0
      : 0;
    const selectedDurationMinutes = isDrivingTest
      ? parseInt(slot.minutes_duration, 10) || slotDuration || this.driveTestDurationValue || 120
      : slotDuration;
    const durationMinutes = isDrivingTest
      ? selectedDurationMinutes + startOffsetMinutes
      : selectedDurationMinutes;
    const actualStartDateTime = startOffsetMinutes > 0
      ? startDateTime.minus({ minutes: startOffsetMinutes })
      : startDateTime;
    const endDateTime = actualStartDateTime.plus({ minutes: durationMinutes });

    return {
      start_time: actualStartDateTime.toISO({ suppressMilliseconds: true }),
      end_time: endDateTime.toISO({ suppressMilliseconds: true }),
      duration_minutes: durationMinutes,
      source_date: selectedDay,
      source_slot: slot,
    };
  }

  buildConflictPreviewBreakTimeRange(selectedDay, shiftBreak, schoolTimeZone) {
    const startMinutes = parseInt(shiftBreak.start_minutes, 10);
    const durationMinutes = parseInt(shiftBreak.duration_minutes, 10);

    if (
      typeof selectedDay !== "string" ||
      Number.isNaN(startMinutes) ||
      Number.isNaN(durationMinutes) ||
      durationMinutes <= 0
    ) {
      return null;
    }

    const startDateTime = this.buildSchoolZoneDateTime(
      selectedDay,
      Math.floor(startMinutes / 60),
      startMinutes % 60,
      schoolTimeZone
    );
    if (!startDateTime) {
      return null;
    }

    const endDateTime = startDateTime.plus({ minutes: durationMinutes });

    return {
      start_time: startDateTime.toISO({ suppressMilliseconds: true }),
      end_time: endDateTime.toISO({ suppressMilliseconds: true }),
      duration_minutes: durationMinutes,
      source_date: selectedDay,
      source_break: shiftBreak,
    };
  }

  buildSchoolZoneDateTime(dateString, hour, minute, timeZone) {
    if (
      typeof dateString !== "string" ||
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      !timeZone
    ) {
      return null;
    }

    const [year, month, day] = dateString.split("-").map((value) => parseInt(value, 10));
    if ([year, month, day].some((value) => Number.isNaN(value))) {
      return null;
    }

    const dateTime = Luxon.DateTime.fromObject(
      {
        year,
        month,
        day,
        hour,
        minute,
        second: 0,
      },
      {
        zone: timeZone,
      }
    );

    return dateTime.isValid ? dateTime : null;
  }

  parseJsonArray(rawValue) {
    if (!rawValue) {
      return [];
    }

    try {
      const parsedValue = JSON.parse(rawValue);
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch {
      return [];
    }
  }

  syncSingleSlotFields() {
    if (
      !this.singleSlotModeValue ||
      !this.hasSingleSlotDateTarget ||
      !this.hasSingleSlotStartTimeTarget
    ) {
      return;
    }

    const selectedDate = this.singleSlotDateTarget.value;
    const selectedTime = this.singleSlotStartTimeTarget.value;
    const slotDuration = parseInt(this.slotDurationTarget.value, 10);

    this.slotDaysInputTarget.value = selectedDate
      ? JSON.stringify([selectedDate])
      : JSON.stringify([]);

    const slotPayload = this.buildSingleSlotPayload({
      selectedTime,
      slotDuration,
      isDrivingTest: this.testRadioButtonTarget.checked,
    });

    this.slottimeselectionTarget.value = slotPayload.lesson;
    this.slottimeselectiontestTarget.value = slotPayload.drivingTest;
  }

  syncSlotDurationInputState() {
    if (!this.hasSlotDurationTarget || !this.hasTestRadioButtonTarget) {
      return;
    }

    const isDrivingTest = this.testRadioButtonTarget.checked;
    const currentDuration = String(this.slotDurationTarget.value || "");
    const durationRow = this.element.querySelector("[id$='single_slot_duration_row']");
    const currentMode = isDrivingTest ? "driving_test" : "lesson";

    durationRow?.style.removeProperty("display");

    if (this.previousMode === "lesson" && currentMode === "driving_test" && currentDuration) {
      this.lastLessonSlotDuration = currentDuration;
    }

    if (this.previousMode === "driving_test" && currentMode === "lesson" && currentDuration) {
      this.lastDrivingTestSlotDuration = currentDuration;
    }

    if (this.previousMode !== currentMode) {
      const nextDuration = currentMode === "driving_test"
        ? this.lastDrivingTestSlotDuration || this.driveTestDurationValue
        : this.lastLessonSlotDuration || currentDuration;

      if (nextDuration) {
        this.slotDurationTarget.value = String(nextDuration);
      }
    }

    const finalDuration = String(this.slotDurationTarget.value || "");
    if (currentMode === "driving_test" && finalDuration) {
      this.lastDrivingTestSlotDuration = finalDuration;
    } else if (finalDuration) {
      this.lastLessonSlotDuration = finalDuration;
    }

    this.previousMode = currentMode;
  }

  syncSelectedSlotDurations() {
    if (
      !this.hasSlotDurationTarget ||
      !this.hasSlottimeselectionTarget ||
      !this.hasSlottimeselectiontestTarget
    ) {
      return;
    }

    const slotDuration = parseInt(this.slotDurationTarget.value, 10);
    if (Number.isNaN(slotDuration) || slotDuration <= 0) {
      return;
    }

    const slotSelectionTarget = this.testRadioButtonTarget.checked
      ? this.slottimeselectiontestTarget
      : this.slottimeselectionTarget;
    const selectedSlots = this.parseJsonArray(slotSelectionTarget.value);
    if (selectedSlots.length === 0) {
      return;
    }

    const updatedSlots = selectedSlots.map((slot) => ({
      ...slot,
      minutes_duration: slotDuration,
    }));

    slotSelectionTarget.value = JSON.stringify(updatedSlots);
  }

  buildSingleSlotPayload({ selectedTime, slotDuration, isDrivingTest }) {
    const [hourValue, minuteValue] = String(selectedTime || "")
      .split(":")
      .map((value) => parseInt(value, 10));

    if (
      Number.isNaN(hourValue) ||
      Number.isNaN(minuteValue) ||
      Number.isNaN(slotDuration) ||
      slotDuration <= 0
    ) {
      return {
        lesson: JSON.stringify([]),
        drivingTest: JSON.stringify([]),
      };
    }

    const lessonSlot = {
      hour: hourValue,
      minute: minuteValue,
      minutes_duration: slotDuration,
      disable_new_students: false,
      disable_current_students: false,
      shift_ids: [],
    };
    const drivingTestSlot = {
      hour: hourValue,
      minute: minuteValue,
      test_hour: hourValue,
      test_minute: minuteValue,
      minutes_duration: slotDuration,
      disable_new_students: false,
      disable_current_students: false,
      shift_ids: [],
    };

    return {
      lesson: JSON.stringify(isDrivingTest ? [] : [lessonSlot]),
      drivingTest: JSON.stringify(isDrivingTest ? [drivingTestSlot] : []),
    };
  }

  shouldTrackConflictPreviewEvent(event) {
    if (!event || !event.target) {
      return false;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    if (
      target.closest("[data-controller='multi-select-pills'] input") ||
      target.closest("[data-controller='small-month-calendar'] input")
    ) {
      return false;
    }

    const targetId = target.id || "";
    const targetName = target.name || "";
    const trackedIdPrefixes = [
      "option-program-",
      "option-classroom-program-",
    ];
    const trackedIds = [
      "premium_instructor_id",
      "slottimeselection",
      "slottimeselectiontest",
      "breakselection",
      "zonesselection",
      "slot_vehicle_id",
      "slot_days_input",
      "single_slot_date",
      "single_slot_start_time",
      "single_slot_duration",
      "option-lesson",
      "option-test",
      "option-observable",
      "option-allow-teens",
      "option-allow-adults",
      "option-allow-new",
      "option-allow-current",
      "slot_duration",
      "break_duration_minutes",
    ];

    if (trackedIds.some((trackedId) => targetId === trackedId || targetId.endsWith(`_${trackedId}`))) {
      return true;
    }

    if (
      trackedIdPrefixes.some(
        (prefix) => targetId.startsWith(prefix) || targetId.includes(`_${prefix}`)
      )
    ) {
      return true;
    }

    return targetName === "option" || targetName.endsWith("_option");
  }

  renderConflictPreviewState(state, { summary, payload, result }) {
    if (!this.hasConflictPreviewResultTarget) {
      return;
    }

    const stateLabels = {
      debouncing: "Debouncing",
      no_send: "No Send",
      sending: "Sending",
      done_sending: "Done sending",
    };

    if (this.hasConflictPreviewStateTarget) {
      this.conflictPreviewStateTarget.textContent = stateLabels[state] || state;
    }

    if (this.hasConflictPreviewSummaryTarget) {
      this.conflictPreviewSummaryTarget.textContent = summary;
      this.conflictPreviewSummaryTarget.classList.toggle(
        "u-hidden",
        state === "debouncing" || state === "sending"
      );
    }

    if (this.hasConflictPreviewPayloadMetaTarget) {
      if (payload) {
        const timeRangeCount = Array.isArray(payload.time_ranges)
          ? payload.time_ranges.length
          : 0;
        const zoneCount = Array.isArray(payload.zone_ids)
          ? payload.zone_ids.length
          : 0;
        const slotCount = Array.isArray(payload.selected_slots)
          ? payload.selected_slots.length
          : 0;
        const dayCount = Array.isArray(payload.selected_days)
          ? payload.selected_days.length
          : 0;

        this.conflictPreviewPayloadMetaTarget.textContent =
          `Instructor: ${payload.instructor_id || "none"} | Vehicle: ${payload.vehicle_id || "none"} | Zones: ${zoneCount} | Days: ${dayCount} | Slots: ${slotCount} | Time ranges: ${timeRangeCount}`;
      } else {
        this.conflictPreviewPayloadMetaTarget.textContent =
          "No payload meta available yet.";
      }
    }

    if (this.hasConflictPreviewPayloadTarget) {
      if (payload) {
        this.conflictPreviewPayloadTarget.innerText = JSON.stringify(
          payload,
          null,
          2
        );
      } else {
        this.conflictPreviewPayloadTarget.innerText =
          "No payload yet for this state.";
      }
    }

    if (this.hasConflictPreviewResultTarget) {
      if (state === "debouncing" || state === "sending") {
        this.conflictPreviewResultTarget.innerHTML =
          this.conflictPreviewSpinnerMarkup();
      } else if (state === "no_send") {
        this.conflictPreviewResultTarget.innerHTML = "";
      } else if (result) {
        this.conflictPreviewResultTarget.innerHTML =
          this.formatConflictPreviewResultMarkup(result);
      } else {
        this.conflictPreviewResultTarget.innerHTML = "";
      }
    }
  }

  formatConflictPreviewResultMarkup(result) {
    if (!result || typeof result !== "object") {
      return "";
    }

    if (result.ok === false) {
      return this.conflictPreviewAlertMarkup({
        severity: "red",
        title: "Conflict preview request failed",
        details: [result.error || "Unknown error."],
      });
    }

    const duplicateMarkup = result.duplicate
      ? this.conflictPreviewAlertMarkup({
          severity: "red",
          title: "Duplicate shift",
          details: [
            "This instructor already has a flexible shift with the same dates and times. Select different dates or times to save.",
          ],
        })
      : "";

    if (result.timing?.empty_input) {
      return (
        duplicateMarkup ||
        this.conflictPreviewSuccessMarkup(
          "No conflicts found for the current selection."
        )
      );
    }

    const groupedPanels = new Map();
    const resourceResults = Array.isArray(result.result) ? result.result : [];

    resourceResults.forEach((resourceResult) => {
      const ranges = Array.isArray(resourceResult.ranges)
        ? resourceResult.ranges
        : [];

      ranges.forEach((rangeResult) => {
        const busyItems = Array.isArray(rangeResult.busy_items)
          ? rangeResult.busy_items
          : [];

        busyItems.forEach((busyItem) => {
          this.addConflictPreviewGroupItem(
            groupedPanels,
            this.conflictPreviewPanelDescriptor(resourceResult, rangeResult, busyItem)
          );
        });
      });
    });

    const panels = Array.from(groupedPanels.values()).map((group, index) =>
      this.conflictPreviewGroupMarkup(group, index)
    );

    if (panels.length === 0) {
      const resourceCount = resourceResults.length;
      const timeRangeCount = result.timing?.resource_counts?.time_ranges ?? 0;

      return (
        duplicateMarkup ||
        this.conflictPreviewSuccessMarkup(
          `No conflicts found. Checked ${resourceCount} resources across ${timeRangeCount} time ranges.`
        )
      );
    }

    return `${duplicateMarkup}<p class="mt0  mb05  u-medium">Conflicts</p> ${panels.join("")}`;
  }

  conflictPreviewPanelDescriptor(resourceResult, rangeResult, busyItem) {
    const resourceType = resourceResult?.resource_type;
    const resourceLabel = this.conflictPreviewResourceLabel(resourceResult);
    const itemTimeLabel = this.conflictPreviewDateTimeLabel(
      busyItem?.start_time,
      busyItem?.end_time
    );
    const itemSortKey = busyItem?.start_time || rangeResult?.start_time || "";

    if (busyItem?.source_type === "ScheduledClosure") {
      return {
        severity: "red",
        title: "School closed",
        resourceLabel,
        itemDetail: `School closed on ${itemTimeLabel}`,
        itemSortKey,
      };
    }

    if (busyItem?.source_type === "DrivingSchoolFlexibleShiftDay") {
      const conflictingInstructor =
        busyItem?.instructor_name ||
        `Instructor ${busyItem?.instructor_id || "unknown"}`;
      return {
        severity: "red",
        title: "Vehicle in use",
        resourceLabel,
        itemDetail: `${conflictingInstructor}'s shift on ${itemTimeLabel}`,
        itemSortKey,
      };
    }

    return {
      severity: "red",
      title: resourceType === "vehicle" ? "Vehicle in use" : "Instructor busy",
      resourceLabel,
      itemDetail: `${this.conflictPreviewItemLabel(busyItem)} on ${itemTimeLabel}`,
      itemSortKey,
    };
  }

  conflictPreviewResourceLabel(resourceResult) {
    const resourceType = resourceResult?.resource_type || "resource";
    const resourceId = resourceResult?.resource_id || "unknown";
    const selectedResourceLabel = this.selectedConflictPreviewResourceLabel(
      resourceType,
      resourceId
    );

    if (selectedResourceLabel) {
      return selectedResourceLabel;
    }

    if (resourceType === "instructor") {
      return `Instructor ${resourceId}`;
    }

    if (resourceType === "vehicle") {
      return `Vehicle ${resourceId}`;
    }

    return `${resourceType} ${resourceId}`;
  }

  selectedConflictPreviewResourceLabel(resourceType, resourceId) {
    const normalizedResourceId = parseInt(resourceId, 10);

    if (Number.isNaN(normalizedResourceId) || normalizedResourceId <= 0) {
      return null;
    }

    if (resourceType === "instructor") {
      const selectedInstructorId = parseInt(this.premiumInstructorIdTarget.value, 10);
      if (selectedInstructorId !== normalizedResourceId) {
        return null;
      }

      return this.selectorDisplayNameForInput(this.premiumInstructorIdTarget);
    }

    if (resourceType === "vehicle") {
      const selectedVehicleId = parseInt(this.slotVehicleIdTarget.value, 10);
      if (selectedVehicleId !== normalizedResourceId) {
        return null;
      }

      return this.selectorDisplayNameForInput(this.slotVehicleIdTarget);
    }

    return null;
  }

  selectorDisplayNameForInput(inputTarget) {
    if (!inputTarget) {
      return null;
    }

    const storedLabel = inputTarget.dataset?.resourceLabel?.trim();
    if (storedLabel) {
      return storedLabel;
    }

    if (!inputTarget.id) {
      return null;
    }

    const selectorRoot = document.querySelector(
      `[data-driving-school-widgets-instructor-selector-input-query-selector="#${inputTarget.id}"]`
    );
    const currentItemName = selectorRoot?.querySelector(
      '[data-driving-school-widgets-instructor-selector-target="currentItemName"]'
    );
    const label = currentItemName?.textContent?.trim();

    return label || null;
  }

  conflictPreviewItemLabel(busyItem) {
    if (!busyItem || typeof busyItem !== "object") {
      return "unknown conflict";
    }

    if (busyItem.source_type === "PremiumInstructorEvent") {
      return `Event "${busyItem.title || "Untitled event"}"`;
    }

    if (busyItem.source_type === "DrivingSchoolVehicleEvent") {
      return `Vehicle event "${busyItem.title || "Untitled event"}"`;
    }

    if (busyItem.source_type === "DrivingSchoolClassroomSession") {
      return "Classroom session";
    }

    if (busyItem.source_type === "DrivingSchoolFlexibleShiftDay") {
      return `Flexible shift ${busyItem.flexible_shift_id || busyItem.source_id}`;
    }

    if (busyItem.source_type === "StudentLesson") {
      return this.humanConflictLessonLabel(busyItem.lesson_type);
    }

    return busyItem.source_type || busyItem.type || "unknown conflict";
  }

  humanConflictLessonLabel(lessonType) {
    const normalizedLessonType = lessonType?.toString().trim().toLowerCase();

    if (!normalizedLessonType) {
      return "Lesson";
    }

    if (
      normalizedLessonType === "us_practical_test" ||
      normalizedLessonType === "practical_test" ||
      normalizedLessonType === "road_test" ||
      normalizedLessonType === "drive_test" ||
      normalizedLessonType.includes("test")
    ) {
      return "Test";
    }

    return "Lesson";
  }

  conflictPreviewDateTimeLabel(startIso, endIso) {
    const schoolTimeZone =
      this.schoolTimeZoneValue ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    const start = startIso
      ? Luxon.DateTime.fromISO(startIso, { setZone: true }).setZone(
          schoolTimeZone
        )
      : null;
    const end = endIso
      ? Luxon.DateTime.fromISO(endIso, { setZone: true }).setZone(
          schoolTimeZone
        )
      : null;

    if (!start?.isValid || !end?.isValid) {
      return "unknown time";
    }

    const startDate = start.toFormat("MMM d");
    const endDate = end.toFormat("MMM d");
    const startTime = start.toFormat("h:mm a");
    const endTime = end.toFormat("h:mm a");

    if (start.hasSame(end, "day")) {
      return `${startDate}, ${startTime}-${endTime}`;
    }

    return `${startDate}, ${startTime} to ${endDate}, ${endTime}`;
  }

  conflictPreviewSpinnerMarkup() {
    return `
      <div class="u-alignMiddle">
        <span class="icon  icon-spinner2  u-animateSpin  u-textGreenMid  u-inlineBlock  u-textLarge  mr05"></span> <span class="u-inlineBlock  ml05">Validating…</span>
      </div>
    `;
  }

  conflictPreviewInfoMarkup(message) {
    return `
      <div class="Panel  p1  u-bgGreyLight  u-borderRadius">
        ${this.escapeHtml(message)}
      </div>
    `;
  }

  conflictPreviewSuccessMarkup(message) {
    return `
      <div class="Panel  Panel--success  p1">
        ${this.escapeHtml(message)}
      </div>
    `;
  }

  addConflictPreviewGroupItem(groupedPanels, descriptor) {
    if (!descriptor) {
      return;
    }

    const groupKey = [
      descriptor.severity || "",
      descriptor.title || "",
      descriptor.resourceLabel || "",
    ].join("::");
    const existingGroup = groupedPanels.get(groupKey);

    if (existingGroup) {
      existingGroup.items.push({
        detail: descriptor.itemDetail,
        sortKey: descriptor.itemSortKey || "",
      });
      return;
    }

    groupedPanels.set(groupKey, {
      severity: descriptor.severity,
      title: descriptor.title,
      resourceLabel: descriptor.resourceLabel,
      items: [
        {
          detail: descriptor.itemDetail,
          sortKey: descriptor.itemSortKey || "",
        },
      ],
    });
  }

  conflictPreviewGroupMarkup(group, index) {
    const severity = group?.severity;
    const panelClass = severity === "yellow" ? "Panel--bright" : "Panel--alert";
    const iconClass = severity === "yellow" ? "u-textYellowDark" : "u-textRed";
    const badgeClass = severity === "yellow" ? "Badge--yellowMid" : "Badge--redMid";
    const sortedItems = Array.isArray(group?.items)
      ? [...group.items].sort((left, right) =>
          (left?.sortKey || "").localeCompare(right?.sortKey || "")
        )
      : [];
    const detailItems = sortedItems
      .filter((item) => item?.detail)
      .map((item) => `<li>${this.escapeHtml(item.detail)}</li>`)
      .join("");
    const itemCount = sortedItems.length;
    const countLabel = `${itemCount}`;
    const summaryId = `conflict-preview-group-${index}`;

    return `
      <details class="Panel  ${panelClass}  p1  mb15">
        <summary id="${summaryId}" class="u-pointer" style="list-style: none;">
          <div class="media" style="align-items: center;">
            <span class="icon  icon-warning  media-left  mr1  ${iconClass}"></span>
            <div class="media-body">
              <strong>${this.escapeHtml(group?.title || "Conflict")}</strong>
              <div class="u-textSmall">${this.escapeHtml(group?.resourceLabel || "Unknown resource")}</div>
            </div>
            <div class="media-right">
              <span class="Badge  ${badgeClass}  Badge--rounded  Badge--tight">${this.escapeHtml(countLabel)}</span>
            </div>
          </div>
        </summary>
        <div class="mt1  pt1">
          <ul class="mt0  mb0">
            ${detailItems}
          </ul>
        </div>
      </details>
    `;
  }

  conflictPreviewAlertMarkup({ severity, title, details }) {
    const panelClass = severity === "yellow" ? "Panel--bright" : "Panel--alert";
    const iconClass = severity === "yellow" ? "u-textYellowDark" : "u-textRed";
    const detailItems = Array.isArray(details)
      ? details
          .filter((detail) => detail)
          .map((detail) => `<li>${this.escapeHtml(detail)}</li>`)
          .join("")
      : "";

    return `
      <div class="Panel  ${panelClass}  p1  mb1">
        <div class="media">
          <span class="icon  icon-warning  media-left  mr1  ${iconClass}"></span>
          <div class="media-body">
            <strong>${this.escapeHtml(title)}</strong>
            <ul class="mt1  mb0">
              ${detailItems}
            </ul>
          </div>
        </div>
      </div>
    `;
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  clearConflictPreviewTimers() {
    if (this.conflictPreviewDebounceTimer) {
      window.clearTimeout(this.conflictPreviewDebounceTimer);
      this.conflictPreviewDebounceTimer = null;
    }

    if (this.conflictPreviewSendingTimer) {
      window.clearTimeout(this.conflictPreviewSendingTimer);
      this.conflictPreviewSendingTimer = null;
    }

    this.cancelConflictPreviewRequest();
  }

  cancelConflictPreviewRequest() {
    if (this.conflictPreviewAbortController) {
      this.conflictPreviewAbortController.abort();
      this.conflictPreviewAbortController = null;
    }
  }

  closeQuickLook(event) {
    event.preventDefault();
    const quickViewElement =
      document.querySelector("#school-shifts-quick-look") ||
      document.querySelector("#school-dashboard-lesson-quick-look");
    if (!quickViewElement) return;
    quickViewElement.classList.add("u-hidden");
  }

  success() {
    this.loaderTarget.classList.add("u-hidden");
    this.formButtonsTarget.classList.add("u-hidden");
    this.successContainerTarget.classList.remove("u-hidden");
  }

  beginLoading() {
    this.loaderTarget.classList.remove("u-hidden");
    this.formButtonsTarget.classList.add("u-hidden");
  }

  endLoading() {
    this.loaderTarget.classList.add("u-hidden");
    this.formButtonsTarget.classList.remove("u-hidden");
  }

  async savePattern(event) {
    this.syncSingleSlotFields();
    this.beginLoading();
    this.apiErrors = [];
    this.dismissDuplicateWarning();

    // Add all the elements to the changed elements array
    this.changedElements.push(
      this.premiumInstructorIdTarget,
      this.slottimeselectionTarget,
      this.slottimeselectiontestTarget,
      this.zonesselectionTarget,
      this.slotVehicleIdTarget,
      this.slotDaysInputTarget,
      this.slotDurationTarget
    );
    if (this.hasBreakselectionTarget) {
      this.changedElements.push(this.breakselectionTarget);
    }
    if (this.hasBreakDurationMinutesTarget) {
      this.changedElements.push(...this.breakDurationMinutesTargets);
    }
    if (this.hasPretestDurationSelectionTarget) {
      this.changedElements.push(this.pretestDurationSelectionTarget);
    }
    if (this.singleSlotModeValue) {
      this.changedElements.push(
        this.singleSlotDateTarget,
        this.singleSlotStartTimeTarget
      );
    }
    const validationErrors = this.validateForm(event);

    if (validationErrors.length > 0) {
      this.endLoading();
      return;
    }


    const authenticity_token = document.querySelector(
      'meta[name="csrf-token"]'
    ).content;

    const isTest = this.testRadioButtonTarget.checked;
    const premium_instructor_id = this.premiumInstructorIdTarget.value;
    let slottimeselection = isTest
      ? this.slottimeselectiontestTarget.value
      : this.slottimeselectionTarget.value;
    const slottimeselectiontest = this.slottimeselectiontestTarget.value;
    const zonesselection = this.zonesselectionTarget.value;
    const pretestDurationSelection = this.hasPretestDurationSelectionTarget
      ? this.pretestDurationSelectionTarget.value
      : "[]";
    const slot_vehicle_id = this.slotVehicleIdTarget.value;
    const slot_days_input = this.slotDaysInputTarget.value;
    const isDrivingTest = this.testRadioButtonTarget.checked;
    const editId = event.currentTarget.dataset.editId;
    const teenAllowed = this.teenCheckboxTarget.checked;
    const adultAllowed = this.adultCheckboxTarget.checked;
    const observable = !isTest && this.observableCheckboxTarget.checked;
    const onlyAdjacentSlots = this.hasAdjacentSlotsOnlyCheckboxTarget
      ? this.adjacentSlotsOnlyCheckboxTarget.checked
      : false;
    const breaks = this.buildBreakPayload();
    const programs = [];
    const classroomPrograms = [];
    this.programCheckboxesTargets.forEach((checkbox) => {
      try {
        if (checkbox.checked) {
          const programIdAsInt = parseInt(checkbox.value, 10);
          programs.push(programIdAsInt);
        }
      } catch {
        // do nothing
      }
    });
    this.classroomProgramCheckboxesTargets.forEach((checkbox) => {
      try {
        if (checkbox.checked) {
          const programIdAsInt = parseInt(checkbox.value, 10);
          classroomPrograms.push(programIdAsInt);
        }
      } catch {
        // do nothing
      }
    });

    const slotDuration = parseInt(this.slotDurationTarget.value, 10);
    if (!Number.isNaN(slotDuration) && slotDuration > 0) {
      let slottimeselectionAsObject = JSON.parse(slottimeselection);
      slottimeselectionAsObject.forEach((slot) => {
        slot.minutes_duration = slotDuration;
      });
      slottimeselection = JSON.stringify(slottimeselectionAsObject);
    }

    const data = {
      premium_instructor_id,
      slottimeselection,
      pretest_duration_selection: pretestDurationSelection,
      zonesselection,
      slot_vehicle_id,
      slot_days_input,
      authenticity_token,
      editId,
      isDrivingTest,
      observable,
      onlyAdjacentSlots,
      breaks,
      programs,
      teenAllowed,
      adultAllowed,
      classroomPrograms,
    };

    try {
      const result = await fetch("/schools/setup/shifts/flexible/new", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (result.ok) {
        this.endLoading();
        this.success();
      } else if (result.status === 409) {
        this.endLoading();
        let duplicateMessage =
          "This instructor already has a flexible shift with the same dates and times. Select different dates or times to save.";
        try {
          const body = await result.json();
          if (body && body.message) duplicateMessage = body.message;
        } catch {
          // keep the default message
        }
        this.showDuplicateWarning(duplicateMessage);
      } else {
        this.endLoading();
        this.apiErrors.push(
          "There was an error saving, please try again later"
        );
        this.validateForm(event);
      }
    } catch (error) {
      this.endLoading();
      this.apiErrors.push("There was an error saving, please try again later");
      this.validateForm(event);
    }
  }

  buildBreakPayload() {
    if (!this.hasBreakselectionTarget) {
      return [];
    }

    const durationMap = this.breakDurationMap();

    return this.parseJsonArray(this.breakselectionTarget.value)
      .map((slot) => {
        const startMinutes = this.startMinutesFromSlot(slot);
        const durationMinutes = parseInt(
          durationMap.get(String(startMinutes)),
          10
        );

        if (
          startMinutes === null ||
          Number.isNaN(durationMinutes) ||
          durationMinutes <= 0
        ) {
          return null;
        }

        return {
          start_minutes: startMinutes,
          duration_minutes: durationMinutes,
        };
      })
      .filter(Boolean);
  }

  startMinutesFromSlot(slot) {
    if (!slot || typeof slot !== "object") {
      return null;
    }

    const hour = parseInt(slot.hour ?? slot.test_hour, 10);
    const minute = parseInt(slot.minute ?? slot.test_minute, 10);

    if (
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return null;
    }

    return hour * 60 + minute;
  }

  timeLabelFromMinutes(startMinutes) {
    const hour = Math.floor(startMinutes / 60);
    const minute = startMinutes % 60;
    const suffix = hour >= 12 ? "pm" : "am";
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;

    return `${displayHour}:${String(minute).padStart(2, "0")} ${suffix}`;
  }

  showDuplicateWarning(message) {
    if (this.hasDuplicateWarningMessageTarget) {
      this.duplicateWarningMessageTarget.textContent = message;
    }
    if (this.hasDuplicateWarningTarget) {
      this.duplicateWarningTarget.classList.remove("u-hidden");
    }
  }

  dismissDuplicateWarning() {
    if (this.hasDuplicateWarningTarget) {
      this.duplicateWarningTarget.classList.add("u-hidden");
    }
  }

  async removePattern(event) {
    this.beginLoading();
    this.apiErrors = [];
    this.validateForm(event);

    const authenticity_token = document.querySelector(
      'meta[name="csrf-token"]'
    ).content;
    const idToDelete = event.currentTarget.dataset.deleteId;
    const premium_instructor_id = this.premiumInstructorIdTarget.value;

    const data = {
      id: idToDelete,
      authenticity_token,
      premium_instructor_id,
    };

    try {
      const result = await fetch("/schools/setup/shifts/flexible/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (result.ok) {
        this.endLoading();
        this.success();
      } else {
        this.endLoading();
        this.apiErrors.push(
          "There was an error removing, please try again later"
        );
        this.validateForm(event);
      }
    } catch (error) {
      this.endLoading();
      this.apiErrors.push(
        "There was an error removing, please try again later"
      );
      this.validateForm(event);
    }
  }
}
